<template>
  <div>
    <BaseHeader
      :title="name"
      :style-variant="17"
    >
      <template
        v-if="!isPlatformBottimmo"
        v-slot:design
      >
        <HeaderDesign />
      </template>

      <template v-slot:desc>
        <p>
          {{ $t('landingpages.body-texts.0') }}
          <br />
          {{ $t('landingpages.body-texts.1') }}
        </p>
      </template>
      <template v-slot:content>
        <v-breadcrumbs :items="breadcrumbItems" />
      </template>
    </BaseHeader>

    <v-row
      v-if="landingpage"
      class="mb-2"
    >
      <v-col
        cols="12"
        lg="4"
        md="6"
        class="py-2 pr-3 pr-md-2"
      >
        <v-card
          border
          class="h-full"
        >
          <v-card-title>
            <h3 class="text-overline text-grey text--darken-1">
              {{ $t('statistics.title') }}
            </h3>
          </v-card-title>
          <v-card-text
            v-if="hasFilter(landingpageType)"
            class="px-6 text-primary"
            style="cursor: pointer"
            @click="handleClick(landingpageType)"
          >
            <span class="text-h2">
              {{ conversionCount }}
            </span>
            <span> x {{ $t(conversionLabel(landingpageType)) }}</span>
          </v-card-text>
          <v-card-text
            v-else
            class="px-6"
          >
            <span class="text-h2">
              {{ conversionCount }}
            </span>
            <span> x {{ conversionLabel(landingpageType) }}</span>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        lg="5"
        md="6"
        class="py-2"
      >
        <v-card
          border
          class="h-full"
        >
          <v-card-title>
            <h3 class="text-overline text-grey text--darken-1">
              {{ $t('landingpage.urls') }}
            </h3>
          </v-card-title>
          <v-card-text class="px-6 text-body-1">
            <div class="d-flex">
              <span class="mr-1 text-grey text--darken-1 text-no-wrap"
                >{{ $t('landingpage.link') }}:
              </span>
              <a
                :href="originalUrl"
                :title="prettyOriginalUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="d-inline-block text-truncate"
              >
                {{ prettyOriginalUrl }}
              </a>
            </div>

            <div class="d-flex">
              <span class="mr-1 text-grey text--darken-1 text-no-wrap"
                >{{ $t('landingpage.custom-url') }}:
              </span>
              <a
                v-if="customUrl"
                :href="customUrl"
                :title="customUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="d-inline-block text-truncate"
              >
                {{ prettyCustomUrl }}
              </a>
              <div
                v-else
                class="d-flex"
              >
                <span class="mr-2 text-grey">{{ $t('landingpage.no-url') }}</span>
                <information-button
                  :title="$t('landingpage.information-button.title')"
                  :content="$t('landingpage.information-button.content')"
                  :link="helpLinks.customUrl"
                  :link-text="$t('landingpage.information-button.link-text')"
                />
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col
        cols="12"
        lg="3"
        class="py-2 pl-3 pl-lg-2"
      >
        <v-card
          border
          class="h-full"
        >
          <v-card-title>
            <h3 class="text-overline text-grey text--darken-1">
              {{ $t('landingpage.integration.title') }}
            </h3>
          </v-card-title>
          <v-card-text class="px-6">
            <p>{{ $t('landingpage.integration.subtitle', { readablePrefix, name }) }}</p>
            <SnippetModal
              :landingpage-slug="slug"
              :readable-prefix="readablePrefix"
              :readable-landingpage="name"
              :company-slug="companySlug"
              :company-site-url="companySiteUrl"
              :landingpage-type="landingpageType"
              small
            />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <Permission permission="material:read">
      <CustomerGuideCard
        v-if="landingpage && isUsCompany && guideMaterialId"
        class="mb-2"
        :guide-material-id="guideMaterialId"
        @draft-dirty="hasUnsavedGuideDraft = $event"
      />
      <v-card
        v-if="showLandingpageMaterials && hasMaterialsToShow"
        border
      >
        <v-card-title>
          <h3 class="text-overline text-grey text--darken-1">
            {{ $t('landingpage.materials.title') }} ({{ materialCount }})
          </h3>
        </v-card-title>
        <v-card-subtitle>
          {{ $t('landingpage.materials.subtitle') }}
        </v-card-subtitle>
        <v-card-text class="px-4">
          <div class="pb-2">
            <template v-if="downloads.length > 0">
              <h3 class="text-overline">
                {{ $t('landingpage.materials.download-documents') }}
              </h3>
              <v-row>
                <v-col
                  v-for="{ previewFile } in coverFiles"
                  :key="previewFile.url"
                  :cols="12"
                  :sm="4"
                  :md="3"
                >
                  <MaterialCard
                    name="Cover"
                    :url="setQueryParams(previewFile.cdnUrl, { attachment: '' })"
                    :thumbnail-url="previewFile.cdnUrl"
                  />
                </v-col>
                <template v-for="material in downloads">
                  <v-col
                    v-for="{ file, previewFile } in material.files"
                    :key="file.url"
                    :cols="12"
                    :sm="4"
                    :md="3"
                  >
                    <MaterialCard
                      :name="material.displayName"
                      :url="file.cdnUrl"
                      :thumbnail-url="previewFile.cdnUrl"
                    />
                  </v-col>
                </template>
              </v-row>
            </template>

            <template v-if="basicAds.length > 0">
              <h3 class="mt-4 text-overline">
                {{ $t('landingpage.materials.basic-ads') }}
              </h3>

              <v-row>
                <template v-for="material in basicAds">
                  <v-col
                    v-for="{ file, previewFile } in material.files"
                    :key="file.url"
                    :cols="12"
                    :sm="4"
                    :md="3"
                  >
                    <MaterialCard
                      :name="material.displayName"
                      :url="file.cdnUrl"
                      :thumbnail-url="previewFile.cdnUrl"
                    />
                  </v-col>
                </template>
              </v-row>
            </template>
          </div>
        </v-card-text>
      </v-card>
    </Permission>

    <v-card
      v-if="showLandingpageMaterials && !hasMaterialsToShow && !showStaticMaterials"
      border
    >
      <no-content-screen
        :title="$t('landingpage.materials.no-content-screen')"
        :sub-title="noContentSubtitle"
      />
    </v-card>

    <v-card
      v-if="showStaticMaterials"
      border
    >
      <v-card-title>
        <h3 class="text-overline text-grey text--darken-1">
          {{ $t('landingpage.materials.title') }} ({{ lightMaterials.length }})
        </h3>
      </v-card-title>
      <v-card-subtitle>
        {{ $t('landingpage.materials.subtitle') }}
      </v-card-subtitle>
      <v-card-text class="px-4">
        <div class="pb-2">
          <template v-if="lightMaterials.length > 0">
            <h3 class="mt-4 text-overline">
              {{ $t('landingpage.materials.basic-ads') }}
            </h3>

            <v-row>
              <template v-for="(material, mIdx) in lightMaterials">
                <v-col
                  v-for="(fileObj, fIdx) in material.files"
                  :key="`${mIdx}-${fIdx}-${fileObj.file.cdnUrl}`"
                  :cols="12"
                  :sm="4"
                  :md="3"
                >
                  <MaterialCard
                    :name="material.displayName"
                    :url="fileObj.file.cdnUrl"
                    :thumbnail-url="fileObj.previewFile.cdnUrl"
                  />
                </v-col>
              </template>
            </v-row>
          </template>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import BaseHeader from '@/components/BaseHeader.vue'
import InformationButton from '@/components/InformationButton.vue'
import NoContentScreen from '@/components/NoContentScreen.vue'
import Permission from '@/components/Permission'
import helpLinks from '@/lib/helpLinks'
import { TOOLS } from '@/lib/tools'
import { setQueryParams } from '@/lib/url'
import brandingMixin from '@/mixins/branding.js'
import feature from '@/mixins/feature'
import { persistFilter, resetAllFilters } from '@/modules/leads/helper/filter'

import GET_AD_MATERIALS from '../contentCreator/creator/queries/getAdMaterials.gql'
import MaterialCard from '../landingpages/MaterialCard'
import SnippetModal from '../landingpages/snippet/index.vue'
import COMPANY_BASE from './CompanyBase.gql'
import CustomerGuideCard from './customer-guide/CustomerGuideCard.vue'
import HeaderDesign from './HeaderDesign.vue'
import LANDINGPAGE from './Landingpage.gql'

const filterOnlyFiles = (material) => material.hasFile

const ARTICLE = {
  VALUATION: 'landingpage.integration.prefix.valuation',
  RETURN_CALL: 'landingpage.integration.prefix.return-call',
  LIFE_ANNUITY: 'landingpage.integration.prefix.life-annuity',
  PARTIAL_SALE: 'landingpage.integration.prefix.partial-sale',
  PLOT_VALUATION: 'landingpage.integration.prefix.plot-valuation',
  QUIZZARD: 'landingpage.integration.prefix.quizzard',
  GUIDE: 'landingpage.integration.prefix.guide',
  CHECKLIST: 'landingpage.integration.prefix.checklist',
}

export default {
  components: {
    SnippetModal,
    MaterialCard,
    CustomerGuideCard,
    BaseHeader,
    NoContentScreen,
    InformationButton,
    HeaderDesign,
    Permission,
  },
  mixins: [feature, brandingMixin],

  data() {
    return {
      helpLinks,
      company: null,
      hasUnsavedGuideDraft: false,
    }
  },

  apollo: {
    landingpage: {
      query: LANDINGPAGE,
      variables() {
        return {
          input: {
            landingpageSlug: this.$route.params.slug,
          },
        }
      },
    },
    company: {
      query: COMPANY_BASE,
      variables() {
        return {
          id: this.$auth.user.companyId,
        }
      },
      skip() {
        return !this.$auth.user?.companyId
      },
      error(error) {
        console.error('Error fetching company:', error)
      },
    },
    lightMaterialsData: {
      query: GET_AD_MATERIALS,
      variables() {
        return {
          input: {
            topicIds: this.landingpage?.topicId ? [this.landingpage.topicId] : [],
            itemsPerPage: 100,
          },
        }
      },
      skip() {
        return !this.showStaticMaterials || !this.landingpage?.topicId
      },
      update: (data) => data?.adMaterials?.adMaterials || [],
    },
  },

  beforeRouteLeave(to, from, next) {
    if (
      !this.hasUnsavedGuideDraft ||
      window.confirm(this.$t('landingpage.customer-guide.editor.leaveConfirmation'))
    ) {
      next()
      return
    }
    next(false)
  },

  computed: {
    showLandingpageMaterials() {
      return Boolean(this.landingpage) && this.hasMaterialsFeature
    },
    showStaticMaterials() {
      return (
        Boolean(this.landingpage) &&
        this.hasStaticMaterialsFeature &&
        (!this.hasMaterialsFeature || !this.hasMaterialsToShow)
      )
    },
    noContentSubtitle() {
      return !this.hasMaterialsFeature
        ? this.$t('landingpage.materials.no-content-subtitle.no-feature-enabled')
        : this.$t('landingpage.materials.no-content-subtitle.feature-enabled')
    },
    name() {
      return this.landingpage?.name || 'Landingpage'
    },
    slug() {
      return this.landingpage?.slug || ''
    },
    conversionCount() {
      return this.landingpage?.conversionCount || 0
    },
    originalUrl() {
      return this.landingpage?.originalUrl || ''
    },
    customUrl() {
      return this.landingpage?.customUrl || ''
    },
    landingpageType() {
      return this.landingpage?.type || 'GUIDE'
    },
    companySlug() {
      return this.company?.slug ?? ''
    },
    companySiteUrl() {
      return this.company?.site.url ?? ''
    },
    isUsCompany() {
      return this.company?.country?.toLowerCase() === 'us'
    },
    materials() {
      return this.landingpage?.materials || []
    },
    guideMaterialId() {
      return this.materials.find((material) => material.assetType === 'GUIDE')?.id || ''
    },
    materialCount() {
      let count = this.coverFiles.length
      this.basicAds.forEach((basicAd) => {
        count += basicAd.files.length
      })
      this.downloads.forEach((download) => {
        count += download.files.length
      })
      return count
    },
    breadcrumbItems() {
      const type =
        this.landingpageType === 'GUIDE'
          ? 'guides'
          : this.landingpageType === 'CHECKLIST'
            ? 'checklists'
            : TOOLS.find((tool) => tool.type === this.landingpageType)
              ? 'tools'
              : 'campaigns'
      return [
        { title: 'Landingpages', to: `/landingpages#${type}`, disabled: false, exact: true },
        { title: 'Details' },
      ]
    },
    coverFiles() {
      const material = this.materials.find(
        (material) => material.type === 'DOWNLOAD' && material.purpose === 'SCREEN'
      )
      if (material && material.files) {
        return material.files
      }
      return []
    },

    downloads() {
      return this.materials
        .filter(filterOnlyFiles)
        .filter((material) => material.type === 'DOWNLOAD')
    },

    basicAds() {
      return this.materials
        .filter(filterOnlyFiles)
        .filter((material) => material.type === 'BASIC_AD')
    },

    hasMaterialsToShow() {
      return this.downloads.length > 0 || this.basicAds.length > 0
    },

    lightMaterials() {
      const data = this.lightMaterialsData || []
      return data.map((m) => ({
        displayName: m.displayName,
        files: [
          {
            file: { cdnUrl: m.url },
            previewFile: { cdnUrl: m.previewUrl },
          },
        ],
      }))
    },

    hasMaterials() {
      return this.materials?.length > 0
    },

    prettyOriginalUrl() {
      return this.originalUrl.replace(/^(https?:\/\/)/, '')
    },

    prettyCustomUrl() {
      if (!this.customUrl) {
        return ''
      }
      return this.customUrl.replace(/^(https?:\/\/)?/, '')
    },

    readablePrefix() {
      const translationKey = ARTICLE[this.landingpageType]
      if (!translationKey) return ''

      try {
        return this.$t(translationKey) || ''
      } catch (error) {
        console.error('Translation error:', error)
        return ''
      }
    },

    hasMaterialsFeature() {
      return (
        this.isFeatureActive(this.featureNames.LANDINGPAGE) &&
        this.getFeature(this.featureNames.LANDINGPAGE)?.config.hasMaterials
      )
    },

    hasStaticMaterialsFeature() {
      return (
        this.isFeatureActive(this.featureNames.LANDINGPAGE) &&
        this.getFeature(this.featureNames.LANDINGPAGE)?.config.hasStaticMaterials
      )
    },
  },
  methods: {
    setQueryParams,
    hasFilter(type) {
      return TOOLS.find((tool) => tool.type === type)
    },
    conversionLabel(type) {
      return TOOLS.find((tool) => tool.type === type)?.action || this.$t('labels.downloaded')
    },
    handleClick(type) {
      resetAllFilters()

      const tool = TOOLS.find((tool) => tool.type === type)
      persistFilter(tool.key, true)

      this.$router.push('/leads?filter')
    },
  },
}
</script>
